import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../components/LoadingOverlay";
import AddressSection from "../../components/AddressSection";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import "../../styles/Form.css";
import { toDateOnly } from "../../utils/dateOnly";
import { formatPhoneNumber } from "../../utils/phone";

function EditUserSelf() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const [isSigned, setIsSigned] = useState(false);
  const [form, setForm] = useState({
    userid: "",
    userid_no: "",
    first_name: "",
    mid_name: "",
    last_name: "",
    gender: "",
    email: "",
    mobile: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    dob: "",
    home_phone: "",
    emp_category: "",
    refer_status: "",
    refer_by: "", // DB field (JSON string)
    refer_by_name: "", // UI helper
    refer_by_id: "", // UI helper
    user_physical_condition: "",
  });

  const [countries, setCountries] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const parseReferBy = (rb) => {
    if (!rb) return { name: "", employeeId: "" };
    try {
      const obj = typeof rb === "string" ? JSON.parse(rb) : rb;
      return { name: obj?.name || "", employeeId: obj?.employeeId || "" };
    } catch {
      return { name: String(rb), employeeId: "" }; // legacy
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${userId}`);
        const { name, employeeId } = parseReferBy(res.data.refer_by);
        setForm({
          ...res.data,
          dob: toDateOnly(res.data.dob),
          refer_by_name: name,
          refer_by_id: employeeId,
        });
      } catch (err) {
        alert("Failed to fetch user data");
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const TASK_ID = 18; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "user_information";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "completed" || status === "almost_expired");
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchData();
    fetchTaskStatus();
  }, [userId, BASE_URL]);

  useEffect(() => {
    const fetchCountires = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/countries`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setCountries(res.data);
      } catch (err) {
        console.error("Failed to fetch countries data", err);
      }
    };
    fetchCountires();
  }, [BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = ["mobile", "home_phone"].includes(name)
      ? formatPhoneNumber(value)
      : value;
    setForm((previous) => ({ ...previous, [name]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaving) return;

    try {
      setIsSaving(true); // 👈 START loading

      const referBy =
        form.refer_status === "Yes"
          ? JSON.stringify({
              name: (form.refer_by_name || "").trim(),
              employeeId: (form.refer_by_id || "").trim(),
            })
          : "";

      const updatedForm = {
        ...form,
        refer_by: referBy,
      };

      delete updatedForm.refer_by_name;
      delete updatedForm.refer_by_id;

      await axios.put(`${BASE_URL}/api/users/${userId}`, updatedForm);

      alert("Profile updated successfully");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Error updating user.";
      alert(msg);
    } finally {
      setIsSaving(false); // 👈 STOP loading
    }
  };

  return (
    <>
      {isSaving && <LoadingOverlay text="Saving changes..." />}
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Pg. 1 - Personal Information
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary px-5"
          onClick={() => navigate("/userhome")}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start" />
          <div className="col-4 text-center">
            <h3 className="mb-0">Pg. 1 - Personal Information</h3>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <fieldset className="border p-3 mb-4">
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Employee ID</label>
                <input
                  type="text"
                  name="userid_no"
                  className="form-control"
                  value={form.userid_no}
                  disabled
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2">Personal Info</legend>
            <div className="row mb-3">
              <div className="col-md-3">
                <label>
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                  disabled={isSigned}
                />
              </div>
              <div className="col-md-3">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="mid_name"
                  className="form-control"
                  value={form.mid_name}
                  onChange={handleChange}
                  disabled={isSigned}
                />
              </div>
              <div className="col-md-3">
                <label>
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                  disabled={isSigned}
                />
              </div>
              <div className="col-md-3">
                <label>Gender</label>
                <select
                  name="gender"
                  className="form-select"
                  value={form.gender}
                  onChange={handleChange}
                  disabled={isSigned}
                >
                  <option value="">Select a gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3">
                <label>
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={isSigned}
                />
              </div>
              <div className="col-md-3">
                <label>
                  Mobile <span className="text-danger">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile"
                  className="form-control"
                  value={formatPhoneNumber(form.mobile)}
                  maxLength={14}
                  inputMode="numeric"
                  onChange={handleChange}
                  required
                  disabled={isSigned}
                />
              </div>
              <div className="col-md-3">
                <label>Home Phone</label>
                <input
                  type="tel"
                  name="home_phone"
                  className="form-control"
                  value={formatPhoneNumber(form.home_phone)}
                  maxLength={14}
                  inputMode="numeric"
                  onChange={handleChange}
                  disabled={isSigned}
                />
              </div>
              <div className="col-md-3">
                <label>
                  Date of Birth <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="dob"
                  className="form-control"
                  value={form.dob}
                  onChange={handleChange}
                  disabled={isSigned}
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3">
                <label>
                  Referred by someone? <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="refer_status"
                  value={form.refer_status}
                  onChange={handleChange}
                  disabled={isSigned}
                  required
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              {form.refer_status === "Yes" ? (
                <>
                  <div className="col-md-3">
                    <label>
                      Referred By <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="refer_by_name"
                      className="form-control"
                      placeholder="Referred by who?"
                      value={form.refer_by_name}
                      onChange={handleChange}
                      disabled={isSigned}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label>Employee ID</label>
                    <input
                      type="text"
                      name="refer_by_id"
                      className="form-control"
                      value={form.refer_by_id}
                      onChange={handleChange}
                      disabled={isSigned}
                    />
                  </div>
                </>
              ) : (
                <div className="col-md-4"></div>
              )}
            </div>
          </fieldset>

          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2">Address</legend>
            <AddressSection
              form={form}
              setForm={setForm}
              countries={countries}
              disabled={isSigned}
            />
          </fieldset>

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary px-5"
              disabled={isSaving}
            >
              Save
            </button>
          </div>
        </form>

        {userId && (
          <>
            <SignatureManager
              taskName="user_information"
              taskId={18}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="user_information"
              taskId={18}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
}

export default EditUserSelf;
